
import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { SubscriptionFeatures } from "@/lib/types";
import { useAuth } from "@/hooks/useAuth";

interface FeatureFlagsContextType {
  features: SubscriptionFeatures | null;
  hasFeature: (featureName: keyof SubscriptionFeatures) => boolean;
  isLoading: boolean;
  error: Error | null;
}

const defaultFeatures: SubscriptionFeatures = {
  currency_conversion: false,
  multiple_currencies: false,
  price_comparison: false,
  grouped_contracts: false,
  contract_services: false,
  reminders: false,
  payment_type_selection: false,
  dashboard: false,
  contract_pdf_email: false
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  features: defaultFeatures,
  hasFeature: () => false,
  isLoading: true,
  error: null
});

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

// Debug mode should be false in production
const DEBUG_FEATURES = false; 

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const { useSubscriptionFeaturesQuery } = useSubscriptions();
  
  const { 
    data: features, 
    isLoading, 
    error 
  } = useSubscriptionFeaturesQuery({
    enabled: isAuthenticated,
    retry: true,
    retryDelay: 1000,
    staleTime: 1000 * 60 * 5, // Consider data stale after 5 minutes
    cacheTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false // Don't refetch on window focus to reduce API calls
  });

  // Log subscription features once when they are initially loaded
  if (DEBUG_FEATURES && features && !isLoading) {
    console.log("Subscription features loaded:", features);
  }

  // Memoize feature checking function to prevent unnecessary re-renders
  const hasFeature = useMemo(() => {
    // Keep track of previous feature accesses to only log changes
    const previousAccess = new Map<string, boolean>();
    
    return (featureName: keyof SubscriptionFeatures): boolean => {
      // If we're still loading or not authenticated, deny access
      if (isLoading || !isAuthenticated) {
        return false;
      }
      
      // If we have subscription features data, use that
      if (features) {
        const hasAccess = features[featureName] || false;
        
        // Only log if DEBUG_FEATURES is enabled and access has changed
        if (DEBUG_FEATURES && previousAccess.get(featureName) !== hasAccess) {
          console.log(`FeatureFlags - Feature ${featureName} access:`, hasAccess);
          previousAccess.set(featureName, hasAccess);
        }
        
        return hasAccess;
      }
      
      return defaultFeatures[featureName] || false;
    };
  }, [features, isLoading, isAuthenticated]);

  const contextValue = useMemo(() => ({
    features: features || defaultFeatures,
    hasFeature,
    isLoading,
    error
  }), [features, hasFeature, isLoading, error]);

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
